import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isNativeWebView, triggerNativeOpenUrl } from '@/utils/nativeBridge';

/**
 * The SmartLev payment page, rendered in place.
 *
 * Not `window.open`: this app runs inside a native WebView, where a popup is
 * either blocked outright or opens somewhere the member cannot get back from.
 * Keeping the page inside the screen means the app never loses its state, and
 * the checkout keeps polling while the member pays.
 *
 * Nothing is read out of the frame. Payment is confirmed by SmartLev's webhook
 * reaching our server, so this is a window onto their page and no more — which
 * is also why cross-origin gives us nothing to work around.
 */
/**
 * Tall enough for a whole SmartLev invoice, and no taller.
 *
 * Every pixel over the real content becomes dead scroll under it, so this is
 * sized from an actual gym drop-in invoice (heading through "download pdf",
 * about 800px) with a margin for a longer item list — not padded for comfort.
 * Raise it only if the Pay button ever falls off the bottom.
 */
const FRAME_CONTENT_HEIGHT = 900;

export function InvoicePaymentFrame({
  url,
  title,
}: {
  url: string;
  title?: string;
}) {
  const { t } = useTranslation();
  /* SmartLev's page takes a moment to paint, and an empty bordered box for
     that moment reads as a failure. `onLoad` fires for a cross-origin iframe
     even though its content cannot be read, which is all this needs. */
  const [loaded, setLoaded] = useState(false);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--tb-space-stack)',
        minHeight: 0,
        flex: 1,
      }}
    >
      {/* `overflow: auto` rather than `hidden`: on iOS a cross-origin iframe
          gets no scrolling of its own and expands to its content, so a hidden
          parent clips it with no way to reach the rest. Auto costs nothing
          where the iframe does scroll itself. */}
      <div
        style={{
          position: 'relative',
          flex: 1,
          minHeight: 320,
          borderRadius: 'var(--tb-radius-xl)',
          border: '1px solid var(--tb-border)',
          background: 'var(--tb-bg)',
          /* Inside the scrollport, not above it. The invoice opens with its
             own heading hard against the top of the page, and the frame is
             the first thing under the title band — so at rest that heading
             butted into the band and read as clipped. Padding out here would
             not have helped: the frame scrolls, so the gap has to live in the
             scrolled content or the first scroll erases it. */
          paddingTop: 'var(--tb-space-section)',
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {!loaded && <PaymentFrameSkeleton />}

        <iframe
          src={url}
          onLoad={() => setLoaded(true)}
          title={title ?? t('checkout.frameTitle', 'Payment')}
          /* The page takes card details and may offer Apple/Google Pay, so it
             needs the payment permission explicitly — an iframe does not
             inherit it. */
          allow="payment *"
          style={{
            width: '100%',
            /* THE IFRAME CARRIES ITS OWN HEIGHT; THE BOX SCROLLS IT.
               iOS WKWebView gives a cross-origin iframe no scrolling of its
               own — at `height: 100%` the page is simply clipped to the box
               and the top of the invoice sits unreachable under the header.
               Sizing the frame past its content and scrolling the parent is
               the only way to reach all of it.

               The height is a guess and has to be: the page is cross-origin,
               so nothing inside it can be measured or posted back. Erring
               long costs some empty space below a short invoice; erring short
               hides the Pay button. `minHeight: 100%` keeps it filling the
               box when the box is taller than this. */
            height: FRAME_CONTENT_HEIGHT,
            /* Never shorter than the box, so a screen taller than the invoice
               shows no gap under it. */
            minHeight: '100%',
            border: 0,
            display: 'block',
            // Kept mounted while it loads so the load actually happens; the
            // skeleton sits over it until it has something to show.
            opacity: loaded ? 1 : 0,
          }}
        />
      </div>

      {/* Some payment pages refuse to be framed. Rather than leave a member
          staring at an empty box with no way forward, there is always a way
          out to the page itself.

          Inside the native WebView a plain `target="_blank"` does nothing —
          the shell has to be asked to open an in-app browser — so this hands
          the URL to native there and stays an ordinary link on the web. */}
      <p
        style={{
          margin: 0,
          textAlign: 'center',
          fontFamily: 'var(--tb-font-body)',
          fontSize: 'var(--tb-text-meta)',
          color: 'var(--tb-text-dim)',
        }}
      >
        {t('checkout.frameTrouble', 'Trouble paying here?')}{' '}
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(event) => {
            if (!isNativeWebView()) return;
            event.preventDefault();
            triggerNativeOpenUrl(url);
          }}
          style={{ color: 'var(--tb-forest)', fontWeight: 'var(--tb-weight-bold)' }}
        >
          {t('checkout.frameOpen', 'Open the payment page')}
        </a>
      </p>
    </div>
  );
}

/**
 * The shape of an invoice, roughly: a couple of line rows, a total, a button.
 * Enough that the wait reads as a page arriving rather than nothing happening.
 */
export function PaymentFrameSkeleton() {
  const bar = (width: string, height = 14) => ({ width, height });

  return (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: 0,
        padding: 'var(--tb-space-card)',
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        background: 'var(--tb-bg)',
      }}
    >
      <div className="tb-skeleton" style={bar('45%', 18)} />
      <div style={{ height: 1, background: 'var(--tb-border)' }} />
      {['70%', '55%', '62%'].map((width) => (
        <div key={width} style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
          <div className="tb-skeleton" style={bar(width)} />
          <div className="tb-skeleton" style={bar('18%')} />
        </div>
      ))}
      <div style={{ height: 1, background: 'var(--tb-border)' }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
        <div className="tb-skeleton" style={bar('30%', 16)} />
        <div className="tb-skeleton" style={bar('22%', 16)} />
      </div>
      <div className="tb-skeleton" style={{ ...bar('100%', 44), marginTop: 'auto' }} />
    </div>
  );
}

export default InvoicePaymentFrame;
